import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  InputAdornment,
  MenuItem,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { submitRecharge, fetchPlansCached, Plan, RechargeResponse } from '../api/middleware';
import { getPlanDisplayValue, getPlanDisplayName, formatCurrency } from '../utils/planDisplay';
import { useSettings } from '../Settings';
import { useTranslation } from '../i18n';

interface RechargeModalProps {
  open: boolean;
  onClose: () => void;
  middlewareUrl: string;
}

export function RechargeModal({ open, onClose, middlewareUrl }: RechargeModalProps) {
  const { settings } = useSettings();
  const { t } = useTranslation();
  const isSurfMVNE = settings.isSurfMVNE === true;
  const [phone, setPhone] = useState('');
  const [planId, setPlanId] = useState<number | ''>('');
  const [plans, setPlans] = useState<Plan[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<RechargeResponse | null>(null);

  useEffect(() => {
    if (!open || !middlewareUrl) return;
    setPlansLoading(true);
    fetchPlansCached(middlewareUrl)
      .then(setPlans)
      .catch(() => setPlans([]))
      .finally(() => setPlansLoading(false));
  }, [open, middlewareUrl]);

  function handleClose() {
    setPhone('');
    setPlanId('');
    setError(null);
    setResult(null);
    setLoading(false);
    onClose();
  }

  function formatPhone(value: string): string {
    const digits = value.replace(/\D/g, '').slice(0, 11);
    if (digits.length <= 2) return digits;
    if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  }

  function getRawPhone(): string {
    return phone.replace(/\D/g, '');
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    const rawPhone = getRawPhone();
    if (rawPhone.length < 10 || rawPhone.length > 11) {
      setError(t.rechargeModal.errorInvalidPhone);
      return;
    }
    if (!planId) {
      setError(t.rechargeModal.errorSelectPlan);
      return;
    }

    setLoading(true);
    try {
      const response = await submitRecharge(middlewareUrl, {
        metodo_pagamento: 'LINK',
        numeros: [{ numero: rawPhone, id_plano: planId as number }],
      });
      setResult(response);
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error?.message ||
        t.rechargeModal.errorGeneric;
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: '16px' },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: 'var(--color-primary, #6B00D6)',
          color: '#fff',
        }}
      >
        <Typography fontWeight={700} fontSize="1.1rem">
          {t.rechargeModal.title}
        </Typography>
        <IconButton onClick={handleClose} sx={{ color: '#fff' }} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2, pb: 1, mt: 0, overflow: 'visible' }}>
        {result ? (
          <Box textAlign="center" py={2}>
            <Typography variant="h6" fontWeight={700} color="success.main" mb={2}>
              {t.rechargeModal.successTitle}
            </Typography>
            {(result.message || result.mensagem) && (
              <Typography variant="body2" color="text.secondary" mb={2}>
                {result.message || result.mensagem}
              </Typography>
            )}
            {(result.link_pagamento || result.recarga?.link_pagamento) && (() => {
              const paymentLink = result.link_pagamento || result.recarga?.link_pagamento || '';
              return (
              <Box display="flex" flexDirection="column" gap={2} mt={2}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    navigator.clipboard.writeText(paymentLink);
                    alert(t.rechargeModal.linkCopied);
                  }}
                  sx={{
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                >
                  {t.rechargeModal.copyLink}
                </Button>
                <Button
                  variant="contained"
                  href={paymentLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    bgcolor: 'var(--color-primary, #6B00D6)',
                    '&:hover': { bgcolor: 'var(--color-primary-dark, #4A0099)' },
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                >
                  {t.rechargeModal.payNow}
                </Button>
              </Box>
              );
            })()}
          </Box>
        ) : (
          <Box component="form" onSubmit={handleSubmit} id="recharge-form">
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <TextField
              label={t.rechargeModal.phoneLabel}
              placeholder={t.rechargeModal.phonePlaceholder}
              value={phone}
              onChange={(e) => setPhone(formatPhone(e.target.value))}
              fullWidth
              required
              sx={{ mb: 2, mt: 2 }}
              InputProps={{
                startAdornment: <InputAdornment position="start">+55</InputAdornment>,
              }}
            />
            <TextField
              select
              label={t.rechargeModal.planLabel}
              value={planId}
              onChange={(e) => setPlanId(Number(e.target.value))}
              fullWidth
              required
              sx={{ mb: 2 }}
              disabled={plansLoading || plans.length === 0}
              helperText={plansLoading ? t.rechargeModal.loadingPlans : undefined}
            >
              {plans.map((p) => {
                // MVNEs Surf não somam o bônus +5GB automaticamente na exibição
                const dataDisplay = getPlanDisplayValue(p, {
                  skipPortabilityBonus: isSurfMVNE,
                });
                const name = getPlanDisplayName(p, dataDisplay);
                const price = formatCurrency(Number(p.valor));
                const label = name !== dataDisplay
                  ? `${name} — ${dataDisplay} — ${price}`
                  : `${dataDisplay} — ${price}`;
                return <MenuItem key={p.id} value={p.id}>{label}</MenuItem>;
              })}
            </TextField>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {result ? (
          <Button onClick={handleClose} variant="outlined" sx={{ borderRadius: '8px', textTransform: 'none' }}>
            {t.common.close}
          </Button>
        ) : (
          <>
            <Button onClick={handleClose} sx={{ textTransform: 'none', color: 'text.secondary' }}>
              {t.common.cancel}
            </Button>
            <Button
              type="submit"
              form="recharge-form"
              variant="contained"
              disabled={loading || plansLoading}
              sx={{
                bgcolor: 'var(--color-primary, #6B00D6)',
                '&:hover': { bgcolor: 'var(--color-primary-dark, #4A0099)' },
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600,
                minWidth: 140,
              }}
            >
              {loading ? <CircularProgress size={22} color="inherit" /> : t.rechargeModal.submitButton}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
